import { ProfileDeactivated, withAccount } from '@/components/AcountLayout';
import { Section } from '@/components/Section';
import { Wizard } from '@/components/Wizard';
import {
  getCategories,
  type AdCategoryItemType,
  type AdItemType,
  type AdType,
} from '@/resource/ad';
import { type LocationItemType } from '@/resource/location';
import { staticProps } from '@/util/staticProps';
import type { ImageUploadType } from '@/util/types';
import { useEffect, useState } from 'react';
import { AdInfoForm } from './AdInfoForm';
import { PreviewForm } from './PreviewForm';
import { SelectCategoryForm } from './SelectCategoryForm';
import { SelectImageForm } from './SelectImageForm';
import { ThankYou } from './ThankYou';

type Props = {
  adType: AdType;
  categories: AdCategoryItemType[];
};

export const CreateRequest = withAccount<Props>(function CreateRequest({
  categories,
  profile,
  adType,
}) {
  const [ad, setAd] = useState<AdItemType | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState<LocationItemType | null>(null);
  const [postalCode, setPostalCode] = useState('');
  const [category, setCategory] = useState<AdCategoryItemType | null>(null);
  const [uploads, setUploads] = useState<ImageUploadType[]>([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setPostalCode(
      profile?.location ? String(profile.location.data.postal_code) : ''
    );
  }, [profile?.location]);

  if (profile.deactivated) {
    return (
      <Section>
        <ProfileDeactivated title="Plaats vraag" />
      </Section>
    );
  }

  if (ad) {
    return (
      <Section>
        <ThankYou ad={ad} />
      </Section>
    );
  }

  return (
    <Section>
      <Wizard
        index={index}
        setIndex={setIndex}
        views={[
          {
            index: 0,
            title: 'Hulpvraag',
            isDisabled: () => false,
            isValid: () =>
              location != null &&
              title.trim().length > 0 &&
              description.trim().length > 0,

            render: ({ isValid }) => (
              <AdInfoForm
                isValid={isValid}
                adType={adType}
                useGeneratedTitle={false}
                postalCode={postalCode}
                setPostalCode={setPostalCode}
                location={location}
                title={title}
                description={description}
                setTitle={setTitle}
                setDescription={setDescription}
                setLocation={setLocation}
                onNext={() => setIndex(1)}
              />
            ),
          },
          {
            index: 1,
            title: 'Categorie',
            isDisabled: () => false,
            isValid: () => category != null,
            render: ({ isValid }) => (
              <SelectCategoryForm
                adType={adType}
                isValid={isValid}
                categories={categories}
                category={category}
                setCategory={setCategory}
                onBack={() => setIndex(0)}
                onNext={() => setIndex(2)}
              />
            ),
          },
          {
            index: 2,
            title: 'Foto',
            isValid: () => true,
            isDisabled: () => false,
            render: () => (
              <SelectImageForm
                uploads={uploads}
                setUploads={setUploads}
                adType={adType}
                onBack={() => setIndex(1)}
                onNext={() => setIndex(3)}
              />
            ),
          },
          {
            index: 3,
            title: 'Preview',
            isValid: () => true,
            isDisabled: () => false,
            render: () =>
              location ? (
                <PreviewForm
                  adType={adType}
                  useGeneratedTitle={false}
                  postalCode={postalCode}
                  location={location}
                  title={title}
                  description={description}
                  profile={profile}
                  uploads={uploads}
                  category={category}
                  activities={[]}
                  onBack={() => setIndex(2)}
                  onCreate={setAd}
                />
              ) : null,
          },
        ]}
      />
    </Section>
  );
});

export const getStaticPropsFor = (adType: AdType) =>
  staticProps.resolve(async () => {
    const categories = await getCategories();

    return {
      props: {
        adType,
        categories,
      },
      revalidate: 10,
    };
  });
